import { useCallback, useEffect, useRef, useState } from 'react';
import { colataLista } from '../../lib/services/colataService';
import { useToast } from '../orizon-ui/Toast';
import { useDebouncedValue } from '../orizon-ui/useDebouncedValue';

/**
 * Lista paginata lato server (.range + count) con ricerca, ordinamento e filtro "disattivati".
 * Va usato dentro ToastProvider (ColataShell).
 */
export const useListaColata = ({ entita, filtri = {}, ordineIniziale, perPagina = 25 }) => {
  const toast = useToast();

  const [pagina, setPagina] = useState(0);
  const [sort, setSort] = useState(ordineIniziale || null);
  const [ricercaInput, setRicercaInput] = useState('');
  const ricerca = useDebouncedValue(ricercaInput, 350);
  const [mostraDisattivati, setMostraDisattivatiState] = useState(false);
  const [dati, setDati] = useState({ righe: [], totale: 0 });
  const [loading, setLoading] = useState(true);
  const [tick, setTick] = useState(0);

  const richiesta = useRef(0);
  const filtriKey = JSON.stringify(filtri || {});

  // cambio di ricerca o filtri esterni -> torna alla prima pagina
  useEffect(() => {
    setPagina(0);
  }, [ricerca, filtriKey]);

  useEffect(() => {
    const numero = ++richiesta.current;
    setLoading(true);

    colataLista(entita, {
      pagina,
      perPagina,
      sort,
      ricerca,
      soloAttivi: !mostraDisattivati,
      filtri: JSON.parse(filtriKey),
    })
      .then((res) => {
        if (numero !== richiesta.current) return; // risposta superata da una richiesta più recente
        if (res.righe.length === 0 && res.totale > 0 && pagina > 0) {
          // pagina rimasta vuota (es. dopo un'eliminazione): torna all'ultima pagina valida
          setPagina(Math.max(0, Math.ceil(res.totale / perPagina) - 1));
          return;
        }
        setDati(res);
        setLoading(false);
      })
      .catch((e) => {
        if (numero !== richiesta.current) return;
        setDati({ righe: [], totale: 0 });
        setLoading(false);
        toast.error(e.message);
      });
    // toast è stabile (useMemo nel provider)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entita, filtriKey, pagina, sort, ricerca, mostraDisattivati, perPagina, tick]);

  const onSort = useCallback((chiave) => {
    setSort((prec) => ({
      key: chiave,
      direction: prec?.key === chiave && prec.direction === 'asc' ? 'desc' : 'asc',
    }));
    setPagina(0);
  }, []);

  const setMostraDisattivati = useCallback((valore) => {
    setMostraDisattivatiState(valore);
    setPagina(0);
  }, []);

  const ricarica = useCallback(() => setTick((t) => t + 1), []);

  return {
    righe: dati.righe,
    totale: dati.totale,
    loading,
    pagina,
    setPagina,
    perPagina,
    sort,
    onSort,
    ricercaInput,
    ricerca,
    setRicercaInput,
    mostraDisattivati,
    setMostraDisattivati,
    ricarica,
  };
};
